/* Copyright (c) 2020-2021 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { useParams } from "react-router-dom";
import { Box } from "@nordcloud/gnui";
import { Table, FlexContainer, QueryPagination } from "~/components";
import { AddContact } from "~/components/Contacts/ContactAdd";
import { useShowError } from "~/components/Error/useShowError";
import { LoaderWrap } from "~/components/LoaderWrap/LoaderWrap";
import { DetailRow } from "~/components/Utils/DetailRow";
import { useUserConsumer } from "~/context/User/User";
import { Contact, RouteParams } from "~/models";
import { isStandardUser } from "~/routing";
import { useAxios, useQueryState } from "~/tools";
import { ContactsListColumns } from "./ContactsList";

function Contacts() {
  const state = useParams<RouteParams>();
  const { customerId } = state;
  const [isLoading, setIsLoading] = React.useState(true);

  const [{ limit, page }] = useQueryState();
  const { showError } = useShowError();
  const { Axios } = useAxios();

  const { role } = useUserConsumer();
  const canEdit = isStandardUser(role);

  const [contacts, setContacts] = React.useState({
    list: [],
    totalCount: "",
  });

  const fetchContacts = React.useCallback(async () => {
    try {
      const { data } = await Axios().get(
        `/customers/${customerId}/contacts?limit=${limit}&page=${page}`
      );
      setContacts(() => ({
        list: data?.externalContacts,
        totalCount: data?.totalCount,
      }));
    } catch (e) {
      showError(e.response);
    } finally {
      setIsLoading(false);
    }
  }, [Axios, customerId, limit, page, showError]);

  const columns = React.useMemo(
    () => ContactsListColumns(fetchContacts, canEdit),
    [canEdit, fetchContacts]
  );

  React.useEffect(() => {
    fetchContacts();
  }, [fetchContacts]);

  const detailsRow = React.useCallback(
    (contact: Contact) => (
      <>
        <DetailRow title="Comment" alignItems="start">
          {contact.comment || "–"}
        </DetailRow>
        <DetailRow title="Rules of engagement" alignItems="start">
          {contact.rulesOfEngagement || "–"}
        </DetailRow>
      </>
    ),
    []
  );

  return (
    <Box>
      <FlexContainer justify="flex-end" css={{ margin: "0 1rem 1rem auto" }}>
        <AddContact refetch={fetchContacts} />
      </FlexContainer>

      <LoaderWrap loading={isLoading}>
        <Table
          columns={columns}
          data={contacts.list || []}
          expandedContent={detailsRow}
          noDataText="No contacts added."
        />
        {contacts.list?.length > 0 && (
          <QueryPagination count={contacts.list.length} />
        )}
      </LoaderWrap>
    </Box>
  );
}

export { Contacts };
